import { useEffect, useState, useCallback } from "react";
import { useSearchParams, useLocation, Link } from "react-router-dom";
import {
  Plus,
  Search,
  ListTodo,
  Kanban,
  CalendarDays,
  CheckCircle2,
  Archive,
  Trash2,
  Settings2,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import TaskList from "@/components/tasks/TaskList";
import TaskBoard from "@/components/tasks/TaskBoard";
import TaskCalendarView from "@/components/tasks/TaskCalendarView";
import TaskDetailDrawer from "@/components/tasks/TaskDetailDrawer";
import TaskFormDialog from "@/components/tasks/TaskFormDialog";
import TaskMetaManager from "@/components/tasks/TaskMetaManager";

// View tabs control the *layout* (list / board / calendar). They no
// longer encode lifecycle state — that's a separate filter below
// (Open / Completed / Archived) so users can switch between board and
// list while still scoping to a single lifecycle bucket.
const VIEWS = [
  { value: "list", label: "List", icon: ListTodo },
  { value: "board", label: "Board", icon: Kanban },
  { value: "calendar", label: "Calendar", icon: CalendarDays },
];

// Lifecycle tabs. "Open" = active (not done, not archived). "Completed"
// = status==done. "Archived" = is_archived==true. The board view
// hides itself for Completed/Archived because the kanban columns are
// status-driven and would be confusing for those lifecycle slices.
const LIFECYCLES = [
  { value: "open", label: "Open", icon: ListTodo },
  { value: "completed", label: "Completed", icon: CheckCircle2 },
  { value: "archived", label: "Archived", icon: Archive },
];

const SCOPES = [
  { value: "me", label: "My tasks" },
  { value: "team", label: "Team tasks" },
  { value: "all", label: "All tasks" },
];

export default function Tasks() {
  const { currentWorkspace } = useWorkspace();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  // Legacy support: ?view=completed used to imply lifecycle=completed.
  // We respect that on first load so old links keep working.
  const legacyView = params.get("view");
  const legacyCompleted = legacyView === "completed" || location.pathname.includes("completed");
  const initialView = legacyView && ["list", "board", "calendar"].includes(legacyView)
    ? legacyView
    : "list";
  const initialLifecycle = params.get("lifecycle") || (legacyCompleted ? "completed" : "open");
  const initialScope = params.get("scope") || "me";
  const [view, setView] = useState(initialView);
  const [lifecycle, setLifecycle] = useState(initialLifecycle);
  const [scope, setScope] = useState(initialScope);
  const [tasks, setTasks] = useState([]);
  const [labels, setLabels] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [members, setMembers] = useState([]);
  const [search, setSearch] = useState("");
  const [filterLabel, setFilterLabel] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterAssignee, setFilterAssignee] = useState("all");
  const [loading, setLoading] = useState(false);
  const [detailTaskId, setDetailTaskId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showMeta, setShowMeta] = useState(false);
  // ─── Bulk selection ────────────────────────────────────────────────
  const [selected, setSelected] = useState(() => new Set());
  const [bulkAction, setBulkAction] = useState(null); // 'archive' | 'delete' | null
  const [bulkBusy, setBulkBusy] = useState(false);

  const fetchTasks = useCallback(async () => {
    if (!currentWorkspace) return;
    setLoading(true);
    try {
      const q = { workspace_id: currentWorkspace.workspace_id, include_subtasks: false };
      // Lifecycle dominates: the backend uses `scope` to slice by
      // archived / completed / active. Within Open, the user's
      // scope select (me / team / all) layers in.
      if (lifecycle === "completed") q.scope = "completed";
      else if (lifecycle === "archived") q.scope = "archived";
      else if (scope !== "all") q.scope = scope;
      if (search.trim()) q.q = search.trim();
      if (filterLabel !== "all") q.label_id = filterLabel;
      if (filterPriority !== "all") q.priority = filterPriority;
      if (filterAssignee !== "all") q.assignee_id = filterAssignee;
      const { data } = await api.get("/tasks", { params: q });
      setTasks(data);
      setSelected(new Set()); // clear selection on reload
    } catch (e) {
      toast.error("Could not load tasks");
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace, scope, lifecycle, search, filterLabel, filterPriority, filterAssignee]);

  const fetchMeta = useCallback(async () => {
    if (!currentWorkspace) return;
    try {
      const [l, m, s] = await Promise.all([
        api.get(`/labels?workspace_id=${currentWorkspace.workspace_id}`),
        api.get(`/workspaces/${currentWorkspace.workspace_id}/members`),
        api.get(`/statuses?workspace_id=${currentWorkspace.workspace_id}`),
      ]);
      setLabels(l.data);
      setMembers(m.data);
      setStatuses(s.data || []);
    } catch {}
  }, [currentWorkspace]);

  useEffect(() => { fetchTasks(); }, [fetchTasks]);
  useEffect(() => { fetchMeta(); }, [fetchMeta]);

  useEffect(() => {
    const next = new URLSearchParams(params);
    next.set("view", view);
    next.set("lifecycle", lifecycle);
    next.set("scope", scope);
    setParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, lifecycle, scope]);

  // When user switches to Completed/Archived while in Board view,
  // fall back to List — board columns are status-keyed and would
  // duplicate the lifecycle filter.
  useEffect(() => {
    if ((lifecycle === "completed" || lifecycle === "archived") && view === "board") {
      setView("list");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lifecycle]);

  const updateTask = async (taskId, patch) => {
    try {
      const { data } = await api.patch(`/tasks/${taskId}`, patch);
      setTasks((prev) => prev.map((t) => (t.task_id === taskId ? data : t)));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not update task");
      throw e;
    }
  };

  const deleteTask = async (taskId) => {
    if (!window.confirm("Delete this task? This cannot be undone.")) return;
    try {
      await api.delete(`/tasks/${taskId}`);
      setTasks((prev) => prev.filter((t) => t.task_id !== taskId));
      toast.success("Task deleted");
      if (detailTaskId === taskId) setDetailTaskId(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not delete");
    }
  };

  const filteredTasks = tasks;

  // ── Bulk selection helpers ───────────────────────────────────────
  const allSelected = filteredTasks.length > 0 &&
    filteredTasks.every((t) => selected.has(t.task_id));
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filteredTasks.map((t) => t.task_id)));
  };
  const clearSelection = () => setSelected(new Set());
  const runBulk = async (action) => {
    const ids = Array.from(selected);
    if (!ids.length || !currentWorkspace) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/tasks/bulk", {
        workspace_id: currentWorkspace.workspace_id,
        task_ids: ids,
        action,
      });
      const verb = action === "archive" ? "archived" : action === "unarchive" ? "restored" : "deleted";
      const n = data.modified ?? data.removed ?? ids.length;
      toast.success(`${n} task${n === 1 ? "" : "s"} ${verb}`);
      setBulkAction(null);
      fetchTasks();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Bulk ${action} failed`);
    } finally {
      setBulkBusy(false);
    }
  };
  const selectionCount = selected.size;
  // Bulk actions are sensible on list / calendar views — board uses
  // drag-and-drop and isn't a multi-select surface.
  const showBulkUI = view === "list" || view === "calendar";

  return (
    <div className="flex flex-col gap-6" data-testid="tasks-page">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="bloq-eyebrow">Tasks</p>
          <h1 className="bloq-headline text-4xl sm:text-5xl mt-2">Turn the conversation into the next step.</h1>
          <p className="text-sm text-[#4a4a52] mt-2">Action items that actually survive the meeting.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowCreate(true)}
            className="bloq-btn-primary inline-flex items-center gap-2"
            data-testid="new-task-button"
          >
            <Plus className="w-4 h-4" /> New task
          </button>
          <Link
            to="/app/tasks/templates"
            className="bloq-btn-secondary inline-flex items-center gap-2"
            data-testid="task-templates-button"
          >
            <Sparkles className="w-4 h-4" /> Task Templates
          </Link>
          <button
            onClick={() => setShowMeta(true)}
            className="bloq-btn-secondary inline-flex items-center gap-2"
            data-testid="manage-task-meta"
          >
            <Settings2 className="w-4 h-4" /> Manage statuses & labels
          </button>
        </div>
      </header>

      {/* Lifecycle tabs — Open / Completed / Archived */}
      <Tabs value={lifecycle} onValueChange={setLifecycle}>
        <TabsList>
          {LIFECYCLES.map((l) => {
            const Icon = l.icon;
            return (
              <TabsTrigger key={l.value} value={l.value} data-testid={`tab-lifecycle-${l.value}`}>
                <Icon className="w-3.5 h-3.5 mr-1.5" /> {l.label}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs value={view} onValueChange={setView}>
          <TabsList>
            {VIEWS.map((v) => {
              const Icon = v.icon;
              // Board view is hidden when not on the Open lifecycle —
              // archived/completed don't map to status columns.
              if (v.value === "board" && lifecycle !== "open") return null;
              return (
                <TabsTrigger key={v.value} value={v.value} data-testid={`tab-view-${v.value}`}>
                  <Icon className="w-3.5 h-3.5 mr-1.5" /> {v.label}
                </TabsTrigger>
              );
            })}
          </TabsList>
        </Tabs>
        <div className="flex flex-wrap items-center gap-2">
          {lifecycle === "open" && (
            <Select value={scope} onValueChange={setScope}>
              <SelectTrigger className="h-9 w-36" data-testid="tasks-scope-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCOPES.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#82828c]" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tasks…"
            className="bloq-input pl-9 h-9"
            data-testid="tasks-search-input"
          />
        </div>
        <Select value={filterLabel} onValueChange={setFilterLabel}>
          <SelectTrigger className="h-9 w-40" data-testid="tasks-label-filter"><SelectValue placeholder="Label" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All labels</SelectItem>
            {labels.map((l) => <SelectItem key={l.label_id} value={l.label_id}>{l.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterPriority} onValueChange={setFilterPriority}>
          <SelectTrigger className="h-9 w-36" data-testid="tasks-priority-filter"><SelectValue placeholder="Priority" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All priorities</SelectItem>
            {["low", "medium", "high", "urgent"].map((p) => (
              <SelectItem key={p} value={p}>{p[0].toUpperCase() + p.slice(1)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filterAssignee} onValueChange={setFilterAssignee}>
          <SelectTrigger className="h-9 w-44" data-testid="tasks-assignee-filter"><SelectValue placeholder="Assignee" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any assignee</SelectItem>
            {members.map((m) => <SelectItem key={m.user_id} value={m.user_id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div>
        {/* Bulk action bar — visible only when at least one task is selected
            (list / completed views only). */}
        {showBulkUI && selectionCount > 0 && (
          <div
            className="mb-3 flex items-center justify-between gap-3 px-4 py-2 bg-[#f5efe6] border border-[#e5e0d6]"
            data-testid="tasks-bulk-bar"
          >
            <p className="text-sm text-[#1a1a1f]" data-testid="tasks-selected-count">
              {selectionCount} selected
            </p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => setBulkAction("archive")}
                disabled={bulkBusy}
                className="bloq-btn-secondary text-xs inline-flex items-center gap-1"
                data-testid="tasks-bulk-archive"
              >
                <Archive className="w-3.5 h-3.5" /> Archive
              </button>
              <button
                onClick={() => setBulkAction("delete")}
                disabled={bulkBusy}
                className="bloq-btn-secondary text-xs inline-flex items-center gap-1 text-[#c43838]"
                data-testid="tasks-bulk-delete"
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
              <button
                onClick={clearSelection}
                className="bloq-btn-secondary text-xs"
                data-testid="tasks-bulk-clear"
              >
                Clear
              </button>
            </div>
          </div>
        )}
        {showBulkUI && filteredTasks.length > 0 && (
          <label
            className="flex items-center gap-2 text-xs text-[#4a4a52] cursor-pointer mb-2"
            data-testid="tasks-select-all-wrap"
          >
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              className="accent-[#005e7a] w-4 h-4"
              data-testid="tasks-select-all"
            />
            Select all visible
          </label>
        )}
        {loading ? (
          <p className="text-sm text-[#82828c]">Loading tasks…</p>
        ) : filteredTasks.length === 0 ? (
          <EmptyState lifecycle={lifecycle} scope={scope} onCreate={() => setShowCreate(true)} />
        ) : view === "list" ? (
          <TaskList
            tasks={filteredTasks}
            labels={labels}
            statuses={statuses}
            currentUser={user}
            onUpdate={updateTask}
            onDelete={deleteTask}
            onOpen={setDetailTaskId}
            selected={selected}
            onToggleSelect={toggleOne}
          />
        ) : view === "board" ? (
          <TaskBoard
            tasks={filteredTasks}
            labels={labels}
            statuses={statuses}
            onUpdate={updateTask}
            onDelete={deleteTask}
            onOpen={setDetailTaskId}
          />
        ) : view === "calendar" ? (
          <TaskCalendarView
            tasks={filteredTasks}
            labels={labels}
            statuses={statuses}
            onUpdate={updateTask}
            onOpen={setDetailTaskId}
          />
        ) : null}
      </div>

      <TaskFormDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        labels={labels}
        members={members}
        onCreated={() => { setShowCreate(false); fetchTasks(); fetchMeta(); }}
      />
      <TaskDetailDrawer
        taskId={detailTaskId}
        onClose={() => setDetailTaskId(null)}
        labels={labels}
        statuses={statuses}
        members={members}
        onUpdated={() => { fetchTasks(); fetchMeta(); }}
        onDeleted={fetchTasks}
      />
      <TaskMetaManager
        open={showMeta}
        onOpenChange={setShowMeta}
        onChanged={() => { fetchMeta(); fetchTasks(); }}
      />

      {/* Bulk archive / delete confirmation */}
      <Dialog open={bulkAction !== null} onOpenChange={(o) => !o && setBulkAction(null)}>
        <DialogContent data-testid="tasks-bulk-confirm">
          <DialogHeader>
            <DialogTitle>
              {bulkAction === "archive" ? "Archive selected tasks?" : "Delete selected tasks?"}
            </DialogTitle>
            <DialogDescription>
              {bulkAction === "archive"
                ? "Archive selected tasks? You can restore them later from the Archived view."
                : "Delete selected tasks? This is permanent — subtasks, comments, and activity will also be removed. Related meetings, notes, and contacts are NOT affected."}
            </DialogDescription>
          </DialogHeader>
          <p className="text-xs text-[#82828c]">
            {selectionCount} task{selectionCount === 1 ? "" : "s"} selected.
          </p>
          <DialogFooter>
            <button
              onClick={() => setBulkAction(null)}
              className="bloq-btn-secondary"
              data-testid="tasks-bulk-cancel"
              disabled={bulkBusy}
            >
              Cancel
            </button>
            <button
              onClick={() => runBulk(bulkAction)}
              className={bulkAction === "delete" ? "bloq-btn-primary bg-[#c43838] hover:bg-[#a82929]" : "bloq-btn-primary"}
              data-testid="tasks-bulk-confirm-action"
              disabled={bulkBusy}
            >
              {bulkBusy ? "Working…" : bulkAction === "archive" ? "Archive" : "Delete"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EmptyState({ lifecycle, scope, onCreate }) {
  let title = "No tasks yet";
  let desc = "Add your first task — bonus points if you give it a due date.";
  if (lifecycle === "completed") {
    title = "Completed tasks will show up here.";
    desc = "Go make future-you proud.";
  } else if (lifecycle === "archived") {
    title = "No archived tasks.";
    desc = "Archive a task and it'll keep its history, just out of the way.";
  } else if (scope === "me") {
    title = "No loose ends. Suspiciously peaceful.";
    desc = "Nothing assigned to you right now. Enjoy it, or add something.";
  } else if (scope === "team") {
    title = "Nothing assigned yet.";
    desc = "Either your team is incredibly organized or everyone is hiding in the notes.";
  }
  return (
    <div className="bloq-card p-10 text-center" data-testid="tasks-empty-state">
      <ListTodo className="w-10 h-10 mx-auto text-[#82828c] mb-3" />
      <p className="bloq-headline text-xl mb-1 text-[#005e7a]">{title}</p>
      <p className="text-sm text-[#82828c] mb-5">{desc}</p>
      <button onClick={onCreate} className="bloq-btn-primary inline-flex items-center gap-2">
        <Plus className="w-4 h-4" /> New task
      </button>
    </div>
  );
}
